import { http } from "./client";
import type { PageParams } from "@/types/api";
import type { LeaveDate, LeaveMode, LeaveRequest, LeaveStatus, LeaveType } from "@/types/domain";

export interface LeaveFilters extends PageParams {
  employee_id?: string;
  department_id?: string;
  branch_id?: string;
  type?: LeaveType | "";
  // One status, or a comma-separated list ("pending_manager,pending_hr") -
  // see parseLeaveStatuses on the backend.
  status?: LeaveStatus | "" | (string & {});
  // Opt-in only (see ListLeaveRequests' own comment) - omitting both leaves
  // every existing caller's unranged behaviour untouched. Added for the
  // Leave Calendar's "every approved request overlapping this month" query.
  from?: string;
  to?: string;
}

// ApplyLeaveInput mirrors the backend's leaveRequestBody: mode picks which
// of the two shapes below is actually sent (an omitted mode, exactly like
// every request built before custom dates existed, means "range") -
// from_date/to_date/half_day_start/half_day_end for "range", custom_dates
// for "custom". Never send both shapes at once; the backend rejects the
// ambiguous combination.
export interface ApplyLeaveInput {
  employee_id: string;
  type: LeaveType;
  mode?: LeaveMode;
  from_date?: string;
  to_date?: string;
  half_day_start?: boolean;
  half_day_end?: boolean;
  custom_dates?: LeaveDate[];
  reason?: string;
}

// LeavePreview is Preview's (POST /leaves/validate) read-only response -
// the requested duration (total_days) and the policy-adjusted balance
// deduction (chargeable_days) are deliberately separate fields, since a
// request spanning a holiday can request more days than it ultimately
// charges.
export interface LeavePreview {
  allowed: boolean;
  message: string;
  total_days: number;
  chargeable_days: number;
  available: number;
  remaining: number;
}

export const leavesApi = {
  list: (filters: LeaveFilters = {}) => http.getPage<LeaveRequest>("/leaves", filters),
  apply: (input: ApplyLeaveInput) => http.post<LeaveRequest>("/leaves", input),
  // Read-only: never reserves balance or persists anything. Used for the
  // "you'll use N days, M remaining" preview shown before submission.
  preview: (input: ApplyLeaveInput) => http.post<LeavePreview>("/leaves/validate", input),
  // "sent_back" is only valid while a request is awaiting HR (pending_hr);
  // the backend rejects it at any other stage. Which stage a plain
  // "approved"/"rejected" affects depends on the request's current status,
  // not on who's calling - see LeaveService.Decide.
  decide: (id: string, decision: "approved" | "rejected" | "sent_back", note?: string) =>
    http.post<LeaveRequest>(`/leaves/${id}/decide`, { status: decision, note }),
  cancel: (id: string) => http.post<{ message: string }>(`/leaves/${id}/cancel`),
};
